import { components } from "@/api-schema";
import { VehicleValuationType } from "@/api-schema-enums";
import { cn } from "@/common/helpers/utils";
import { DeviceType } from "@/common/hooks/use-device";
import { BrandLogo, BrandLogoVariant } from "@/components/brand-logo/brand-logo";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { ImplementedColumnDef } from "@/components/data-table/types";
import { PlaceholderLogo, PlaceholderLogoSize } from "@/components/placeholder-logo/placeholder-logo";
import { EMPTY_VALUE_PLACEHOLDER } from "@/components/ui/empty-value";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { VehicleValuationStatusBadge } from "@/components/vehicle-valuation/vehicle-valuation-status-badge";
import { roundVehicleValuationPriceForDisplay } from "@/modules/fab/vehicle-valuation/_helpers/vehicle-valuation-derived-values";
import { getVehicleValuationTypeLabel } from "@/modules/fab/vehicle-valuation/_helpers/vehicle-valuation-metadata";
import i18next from "i18next";
import { type ReactNode } from "react";
import { ValuationNumberFormat, ValuationValueCell } from "./_components/valuation-value-cell";
import { renderVehicleValuationTitle } from "./_helpers/render-vehicle-valuation-title";

type VehicleValuation = components["schemas"]["VehicleValuation"];
type VehicleValuationRow = VehicleValuation & RowWithIdentifier;
type TradeGrossValueSource = Pick<
  VehicleValuation,
  "leaseBuyoutAmount" | "confirmedTradeGross" | "tradeGross" | "valuationType"
>;

function getTradeGrossSortValue(valuation: TradeGrossValueSource): number | null | undefined {
  return valuation.valuationType === VehicleValuationType.LeasingBuyout
    ? valuation.leaseBuyoutAmount
    : (valuation.confirmedTradeGross ?? valuation.tradeGross);
}

function getConfirmedTradeGrossSortValue(valuation: TradeGrossValueSource): number | null | undefined {
  return valuation.valuationType === VehicleValuationType.LeasingBuyout
    ? valuation.leaseBuyoutAmount
    : (valuation.confirmedTradeGross ?? valuation.tradeGross);
}

function renderCurrencyValue(value: number | null | undefined): ReactNode {
  return automaticCellRenderer(value, { numberFormat: "currency", numberDecimals: 2 });
}

function getSpecialValueHintMessages(valuation: TradeGrossValueSource): string[] {
  const messages: string[] = [];

  if (valuation.valuationType === VehicleValuationType.LeasingBuyout) {
    messages.push(i18next.t("fab:VehicleValuationOverviewLeaseBuyoutAmountHint"));
  }

  if (valuation.valuationType === VehicleValuationType.WholesaleBuyer) {
    messages.push(i18next.t("fab:VehicleValuationTypeWholesaleBuyerHint"));
  }

  return messages;
}

function renderSpecialValueTooltipContent(messages: string[]): ReactNode {
  if (messages.length === 1) {
    return messages[0];
  }

  return (
    <div className="max-w-72 space-y-1">
      {messages.map((message) => (
        <p key={message}>{message}</p>
      ))}
    </div>
  );
}

function renderValueWithSpecialCaseTooltip(value: ReactNode, messages: string[], className?: string): ReactNode {
  if (messages.length === 0) {
    return className ? <span className={className}>{value}</span> : value;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn("cursor-help underline decoration-dotted underline-offset-2", className)}>{value}</span>
        </TooltipTrigger>
        <TooltipContent>{renderSpecialValueTooltipContent(messages)}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function renderHighlightedCurrencyValue(value: number | null | undefined, messages: string[] = []): ReactNode {
  const renderedValue = renderCurrencyValue(value);

  if (value == null) {
    return renderValueWithSpecialCaseTooltip(renderedValue, messages);
  }

  return renderValueWithSpecialCaseTooltip(renderedValue, messages, "text-primary font-semibold");
}

function renderTradeGrossValue(valuation: TradeGrossValueSource): ReactNode {
  const specialValueHintMessages = getSpecialValueHintMessages(valuation);

  if (valuation.valuationType === VehicleValuationType.LeasingBuyout) {
    return renderHighlightedCurrencyValue(valuation.leaseBuyoutAmount, specialValueHintMessages);
  }

  return (
    <ValuationValueCell
      confirmed={valuation.confirmedTradeGross}
      generated={valuation.tradeGross}
      numberFormat={ValuationNumberFormat.Currency}
      numberDecimals={2}
      confirmedValueTransform={roundVehicleValuationPriceForDisplay}
      additionalValueTooltipContent={
        specialValueHintMessages.length > 0 ? renderSpecialValueTooltipContent(specialValueHintMessages) : undefined
      }
    />
  );
}

function renderConfirmedTradeGrossValue(valuation: TradeGrossValueSource): ReactNode {
  const specialValueHintMessages = getSpecialValueHintMessages(valuation);

  if (valuation.valuationType === VehicleValuationType.LeasingBuyout) {
    return renderHighlightedCurrencyValue(valuation.leaseBuyoutAmount, specialValueHintMessages);
  }

  return renderValueWithSpecialCaseTooltip(
    renderCurrencyValue(roundVehicleValuationPriceForDisplay(valuation.confirmedTradeGross)),
    specialValueHintMessages,
  );
}

function getTranslatedVehicleValuationTypeLabel(valuationType: VehicleValuation["valuationType"]): string | undefined {
  return getVehicleValuationTypeLabel(valuationType, (key) => i18next.t(key));
}

function renderVehicleValuationType(value: VehicleValuation["valuationType"]): ReactNode {
  return automaticCellRenderer(getTranslatedVehicleValuationTypeLabel(value));
}

export function buildVehicleValuationTableColumns(
  canManageVehicleValuation: boolean,
): ImplementedColumnDef<VehicleValuationRow, unknown>[] {
  return [
    {
      id: "mobileSummary",
      size: 320,
      visibility: [DeviceType.Mobile],
      enableSorting: false,
      header: () => i18next.t("common:Vehicle"),
      cell: ({ row }) => {
        const valuation = row.original;
        const brand = valuation.vehicle?.modelGeneration?.modelFamily?.brand;

        return (
          <div className="flex w-full flex-col gap-2">
            <div className="flex items-center gap-2">
              {brand ? (
                <BrandLogo brand={brand} variant={BrandLogoVariant.COMPACT} />
              ) : (
                <PlaceholderLogo label={i18next.t("fab:UnknownBrand")} size={PlaceholderLogoSize.Sm} />
              )}
              <div className="min-w-0">
                <div className="truncate font-medium">{renderVehicleValuationTitle(valuation)}</div>
                <div className="text-muted-foreground truncate font-mono text-xs">
                  {valuation.vinUsed ?? EMPTY_VALUE_PLACEHOLDER}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <VehicleValuationStatusBadge status={valuation.status} />
            </div>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <div>
                <dt className="text-muted-foreground">{i18next.t("common:RegistrationDate")}</dt>
                <dd>{automaticCellRenderer(valuation.registrationDateUsed)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{i18next.t("masterdata:Mileage")}</dt>
                <dd>{automaticCellRenderer(valuation.mileageUsed)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{i18next.t("masterdata:NumberOfHolders")}</dt>
                <dd>{automaticCellRenderer(valuation.numberOfHoldersUsed)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{i18next.t("fab:VehicleValuationType")}</dt>
                <dd>{renderVehicleValuationType(valuation.valuationType)}</dd>
              </div>
              {canManageVehicleValuation ? (
                <>
                  <div>
                    <dt className="text-muted-foreground">{i18next.t("masterdata:TradeGross")}</dt>
                    <dd>{renderTradeGrossValue(valuation)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{i18next.t("masterdata:SalesGross")}</dt>
                    <dd>
                      <ValuationValueCell
                        confirmed={valuation.confirmedSalesGross}
                        generated={valuation.salesGross}
                        numberFormat={ValuationNumberFormat.Currency}
                        numberDecimals={2}
                        confirmedValueTransform={roundVehicleValuationPriceForDisplay}
                      />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{i18next.t("masterdata:MarginGross")}</dt>
                    <dd>
                      {automaticCellRenderer(valuation.marginGross, { numberFormat: "currency", numberDecimals: 2 })}
                    </dd>
                  </div>
                </>
              ) : (
                <div>
                  <dt className="text-muted-foreground">{i18next.t("masterdata:ConfirmedTradeGross")}</dt>
                  <dd>{renderConfirmedTradeGrossValue(valuation)}</dd>
                </div>
              )}
            </dl>
          </div>
        );
      },
    },
    {
      id: "vehicleInfo",
      size: 260,
      visibility: [DeviceType.Tablet, DeviceType.Desktop],
      enableSorting: false,
      header: () => i18next.t("common:Vehicle"),
      cell: ({ row }) => {
        const valuation = row.original;
        const brand = valuation.vehicle?.modelGeneration?.modelFamily?.brand;

        return (
          <div className="flex items-start gap-3">
            {brand ? (
              <BrandLogo brand={brand} className="mt-0.5 shrink-0" />
            ) : (
              <PlaceholderLogo label={i18next.t("fab:UnknownBrand")} className="mt-0.5 shrink-0" />
            )}
            <div className="min-w-0">
              <div className="font-medium wrap-break-word">{renderVehicleValuationTitle(valuation)}</div>
              <div className="text-muted-foreground truncate font-mono text-xs">
                {valuation.vinUsed ?? EMPTY_VALUE_PLACEHOLDER}
              </div>
              <dl className="mt-1 flex flex-col gap-y-0.5 text-xs">
                <div className="flex items-baseline gap-1">
                  <dt className="text-muted-foreground shrink-0">{i18next.t("common:RegistrationDate")}:</dt>
                  <dd className="truncate">{automaticCellRenderer(valuation.registrationDateUsed)}</dd>
                </div>
                <div className="flex items-baseline gap-1">
                  <dt className="text-muted-foreground shrink-0">{i18next.t("masterdata:Mileage")}:</dt>
                  <dd className="truncate">{automaticCellRenderer(valuation.mileageUsed)}</dd>
                </div>
                <div className="flex items-baseline gap-1">
                  <dt className="text-muted-foreground shrink-0">{i18next.t("masterdata:NumberOfHolders")}:</dt>
                  <dd className="truncate">{automaticCellRenderer(valuation.numberOfHoldersUsed)}</dd>
                </div>
              </dl>
            </div>
          </div>
        );
      },
    },
    ...(canManageVehicleValuation
      ? [
          {
            id: "tradeGrossCompact",
            accessorFn: (row: VehicleValuationRow) => getTradeGrossSortValue(row),
            size: 140,
            visibility: [DeviceType.Tablet, DeviceType.Desktop],
            enableSorting: true,
            header: () => i18next.t("masterdata:TradeGross"),
            cell: ({ row }: { row: { original: VehicleValuationRow } }) => renderTradeGrossValue(row.original),
          },
          {
            id: "salesGrossCompact",
            accessorFn: (row: VehicleValuationRow) => row.confirmedSalesGross ?? row.salesGross,
            size: 140,
            visibility: [DeviceType.Tablet, DeviceType.Desktop],
            enableSorting: true,
            header: () => i18next.t("masterdata:SalesGross"),
            cell: ({ row }: { row: { original: VehicleValuationRow } }) => (
              <ValuationValueCell
                confirmed={row.original.confirmedSalesGross}
                generated={row.original.salesGross}
                numberFormat={ValuationNumberFormat.Currency}
                numberDecimals={2}
                confirmedValueTransform={roundVehicleValuationPriceForDisplay}
              />
            ),
          },
        ]
      : [
          {
            id: "tradeNetCompact",
            accessorFn: (row: VehicleValuationRow) => getConfirmedTradeGrossSortValue(row),
            size: 140,
            visibility: [DeviceType.Tablet, DeviceType.Desktop],
            enableSorting: true,
            header: () => i18next.t("masterdata:ConfirmedTradeGross"),
            cell: ({ row }: { row: { original: VehicleValuationRow } }) => renderConfirmedTradeGrossValue(row.original),
          },
        ]),
    {
      id: "vehicleDetails",
      size: 260,
      visibility: [DeviceType.Widescreen],
      enableSorting: false,
      header: () => i18next.t("common:Vehicle"),
      cell: ({ row }) => {
        const valuation = row.original;
        const brand = valuation.vehicle?.modelGeneration?.modelFamily?.brand;

        return (
          <div className="flex items-start gap-3">
            {brand ? (
              <BrandLogo brand={brand} className="mt-0.5 shrink-0" />
            ) : (
              <PlaceholderLogo label={i18next.t("fab:UnknownBrand")} className="mt-0.5 shrink-0" />
            )}
            <div className="min-w-0">
              <div className="font-medium wrap-break-word">{renderVehicleValuationTitle(valuation)}</div>
              <div className="text-muted-foreground truncate font-mono text-xs">
                {valuation.vinUsed ?? EMPTY_VALUE_PLACEHOLDER}
              </div>
              <dl className="mt-1 flex flex-col gap-y-0.5 text-xs">
                <div className="flex items-baseline gap-1">
                  <dt className="text-muted-foreground shrink-0">{i18next.t("common:RegistrationDate")}:</dt>
                  <dd className="truncate">{automaticCellRenderer(valuation.registrationDateUsed)}</dd>
                </div>
                <div className="flex items-baseline gap-1">
                  <dt className="text-muted-foreground shrink-0">{i18next.t("masterdata:Mileage")}:</dt>
                  <dd className="truncate">{automaticCellRenderer(valuation.mileageUsed)}</dd>
                </div>
                <div className="flex items-baseline gap-1">
                  <dt className="text-muted-foreground shrink-0">{i18next.t("masterdata:NumberOfHolders")}:</dt>
                  <dd className="truncate">{automaticCellRenderer(valuation.numberOfHoldersUsed)}</dd>
                </div>
              </dl>
            </div>
          </div>
        );
      },
    },
    ...(canManageVehicleValuation
      ? [
          {
            id: "tradeGross",
            accessorFn: (row: VehicleValuationRow) => getTradeGrossSortValue(row),
            size: 140,
            visibility: [DeviceType.Widescreen],
            enableSorting: true,
            header: () => i18next.t("masterdata:TradeGross"),
            cell: ({ row }: { row: { original: VehicleValuationRow } }) => renderTradeGrossValue(row.original),
          },
          {
            id: "salesGross",
            accessorFn: (row: VehicleValuationRow) => row.confirmedSalesGross ?? row.salesGross,
            size: 140,
            visibility: [DeviceType.Widescreen],
            enableSorting: true,
            header: () => i18next.t("masterdata:SalesGross"),
            cell: ({ row }: { row: { original: VehicleValuationRow } }) => (
              <ValuationValueCell
                confirmed={row.original.confirmedSalesGross}
                generated={row.original.salesGross}
                numberFormat={ValuationNumberFormat.Currency}
                numberDecimals={2}
                confirmedValueTransform={roundVehicleValuationPriceForDisplay}
              />
            ),
          },
          {
            id: "marginGross",
            accessorFn: (row: VehicleValuationRow) => row.marginGross,
            size: 120,
            visibility: [DeviceType.Widescreen],
            enableSorting: true,
            header: () => i18next.t("masterdata:MarginGross"),
            cell: ({ row }: { row: { original: VehicleValuationRow } }) =>
              automaticCellRenderer(row.original.marginGross, { numberFormat: "currency", numberDecimals: 2 }),
          },
        ]
      : [
          {
            id: "tradeNet",
            accessorFn: (row: VehicleValuationRow) => getConfirmedTradeGrossSortValue(row),
            size: 140,
            visibility: [DeviceType.Widescreen],
            enableSorting: true,
            header: () => i18next.t("masterdata:ConfirmedTradeGross"),
            cell: ({ row }: { row: { original: VehicleValuationRow } }) => renderConfirmedTradeGrossValue(row.original),
          },
        ]),
    {
      id: "valuationType",
      accessorFn: (row: VehicleValuationRow) =>
        getTranslatedVehicleValuationTypeLabel(row.valuationType) ?? row.valuationType ?? "",
      size: 140,
      visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
      enableSorting: true,
      header: () => i18next.t("fab:VehicleValuationType"),
      cell: ({ row }) => renderVehicleValuationType(row.original.valuationType),
    },
    {
      id: "status",
      accessorFn: (row) => row.status,
      size: 130,
      visibility: [DeviceType.Widescreen],
      enableSorting: true,
      header: () => i18next.t("common:Status"),
      cell: ({ row }) => <VehicleValuationStatusBadge status={row.original.status} />,
    },
    {
      id: "statusCompact",
      accessorFn: (row) => row.status,
      size: 130,
      visibility: [DeviceType.Tablet, DeviceType.Desktop],
      enableSorting: true,
      header: () => i18next.t("common:Status"),
      cell: ({ row }) => <VehicleValuationStatusBadge status={row.original.status} />,
      align: "center",
    },
    {
      id: "createdAt",
      accessorFn: (row) => row.createdAt,
      size: 130,
      visibility: [DeviceType.Widescreen],
      enableSorting: true,
      header: () => i18next.t("common:CreatedAt"),
      cell: ({ row }) => {
        const date = automaticCellRenderer(row.original.createdAt, {
          dateFormat: "date",
        });
        const time = automaticCellRenderer(row.original.createdAt, {
          dateFormat: "time",
        });
        return (
          <div className="flex flex-col">
            <span>{date}</span>
            <span>{time}</span>
          </div>
        );
      },
    },
  ];
}

export const vehicleValuationTableDefaultColumns = buildVehicleValuationTableColumns(false);
